import { coreApiWithInterceptors } from '../../../services/axiosInterceptor';
import {
  APICLOSETGroupsDefaultParams,
  DeleteCLOSETGroupsCommentsParams,
  DeleteCLOSETGroupsManagersParams,
  GetCLOSETGroupInfoResponse,
  GetCLOSETGroupsBillingHistoryParams,
  GetCLOSETGroupsBillingHistoryResponse,
  GetCLOSETGroupsClofabFeatureResponse,
  GetCLOSETGroupsCommentsParams,
  GetCLOSETGroupsCommentsResponse,
  GetCLOSETGroupsListResponse,
  GetCLOSETGroupsManagersResponse,
  GetCLOSETGroupsMembersSummaryResponse,
  GetCLOSETGroupsParams,
  GetCLOSETGroupsSubscriptionHistoryParams,
  GetCLOSETGroupsSubscriptionHistoryResponse,
  GetCLOSETGroupsUsageResponse,
  PatchCLOSETGroupsClofabFeatureParams,
  PostCLOSETGroupsUsageParams,
  PostCLOSETGroupsUsageResponse,
  PostCLOSETGroupsCommentsParams,
  PostCLOSETGroupsManagersParams,
  PostCLOSETGroupsManagersResponse,
  PutCLOSETGroupsCommentsParams,
} from '../../../types/api/groups/closet';
import qs from 'qs';
import { CLOSETPaymentCurrency } from 'types/groups';

const CLOSET_GROUPS_URL = '/closet/groups';

// [EN] Named and exported so the outgoing query string is assertable as a value,
//      not only observable through a live request.
// [KO] 나가는 쿼리스트링을 실제 요청으로만 관찰하지 않고 값으로 검증할 수 있도록
//      이름을 붙여 밖으로 뺐다.
export const serializeGroupsParams = (params: GetCLOSETGroupsParams): string =>
  qs.stringify(params, { arrayFormat: 'repeat' });

export const getClosetGroups = async (
  params: GetCLOSETGroupsParams,
): Promise<GetCLOSETGroupsListResponse> => {
  const { data } = await coreApiWithInterceptors.get(`${CLOSET_GROUPS_URL}`, {
    params,
    // [EN] `as`: axios types this argument as `Record<string, any>`, which does not
    //      assign to the domain type. Only `GetCLOSETGroupsParams` ever reaches it.
    // [KO] `as` 인 이유: axios 가 이 인자를 `Record<string, any>` 로 잡아 도메인 타입에
    //      대입되지 않는다. 실제로 도달하는 값은 `GetCLOSETGroupsParams` 뿐이다.
    paramsSerializer: params => serializeGroupsParams(params as GetCLOSETGroupsParams),
  });
  return data;
};

export const getClosetGroupsManagers = async (): Promise<GetCLOSETGroupsManagersResponse> => {
  const { data } = await coreApiWithInterceptors.get(`${CLOSET_GROUPS_URL}/managers`, {
    headers: { maxAge: 31536000 },
  });
  return data;
};

export const getGroupInfo = async ({
  groupId,
}: {
  groupId: string;
}): Promise<GetCLOSETGroupInfoResponse> => {
  const { data } = await coreApiWithInterceptors.get(`${CLOSET_GROUPS_URL}/${groupId}`);
  return data;
};

export const postClosetGroupsManagers = async (
  params: PostCLOSETGroupsManagersParams,
): Promise<PostCLOSETGroupsManagersResponse> => {
  const { data } = await coreApiWithInterceptors.post(`${CLOSET_GROUPS_URL}/managers`, params);
  return data;
};

export const deleteClosetGroupsManagers = async ({
  groupId,
  userId,
}: DeleteCLOSETGroupsManagersParams): Promise<void> => {
  return await coreApiWithInterceptors.delete(`${CLOSET_GROUPS_URL}/${groupId}/managers/${userId}`);
};

export const getClosetGroupsMembersSummary = async ({
  groupId,
}: APICLOSETGroupsDefaultParams): Promise<GetCLOSETGroupsMembersSummaryResponse> => {
  const { data } = await coreApiWithInterceptors.get(
    `${CLOSET_GROUPS_URL}/${groupId}/members/summary`,
  );
  return data;
};

export const getClosetGroupsUsage = async ({
  groupId,
}: APICLOSETGroupsDefaultParams): Promise<GetCLOSETGroupsUsageResponse> => {
  const { data } = await coreApiWithInterceptors.get(`${CLOSET_GROUPS_URL}/${groupId}/usage`);
  return data;
};

export const getClosetGroupsSubscriptionHistory = async ({
  groupId,
  ...params
}: GetCLOSETGroupsSubscriptionHistoryParams): Promise<GetCLOSETGroupsSubscriptionHistoryResponse> => {
  const { data } = await coreApiWithInterceptors.get(
    `${CLOSET_GROUPS_URL}/${groupId}/subscriptions/histories`,
    { params },
  );
  return data;
};

export const getClosetGroupsBillingHistory = async ({
  groupId,
  ...params
}: GetCLOSETGroupsBillingHistoryParams): Promise<GetCLOSETGroupsBillingHistoryResponse> => {
  const { data } = await coreApiWithInterceptors.get(
    `${CLOSET_GROUPS_URL}/${groupId}/billings/histories`,
    { params },
  );
  return data;
};

export const getClosetGroupsComments = async ({
  groupId,
  ...params
}: GetCLOSETGroupsCommentsParams): Promise<GetCLOSETGroupsCommentsResponse> => {
  const { data } = await coreApiWithInterceptors.get(`${CLOSET_GROUPS_URL}/${groupId}/comments`, {
    params,
  });
  return data;
};

export const postClosetGroupsComments = async ({
  groupId,
  description,
}: PostCLOSETGroupsCommentsParams) => {
  const { data } = await coreApiWithInterceptors.post(`${CLOSET_GROUPS_URL}/${groupId}/comments`, {
    description,
  });
  return data;
};

export const deleteClosetGroupsComments = async ({
  commentId,
  groupId,
}: DeleteCLOSETGroupsCommentsParams) => {
  const { data } = await coreApiWithInterceptors.delete(
    `${CLOSET_GROUPS_URL}/${groupId}/comments/${commentId}`,
  );
  return data;
};

export const putClosetGroupsComments = async ({
  commentId,
  groupId,
  description,
}: PutCLOSETGroupsCommentsParams) => {
  const { data } = await coreApiWithInterceptors.put(
    `${CLOSET_GROUPS_URL}/${groupId}/comments/${commentId}`,
    { description },
  );
  return data;
};

export const patchClosetGroupsCurrency = async ({
  groupId,
  currency,
}: {
  groupId: string;
  currency: CLOSETPaymentCurrency;
}) => {
  const { data } = await coreApiWithInterceptors.patch(`${CLOSET_GROUPS_URL}/${groupId}/currency`, {
    currency,
  });
  return data;
};

export const postClosetGroupsUsage = async ({
  groupId,
  ...params
}: PostCLOSETGroupsUsageParams): Promise<PostCLOSETGroupsUsageResponse> => {
  const { data } = await coreApiWithInterceptors.post(
    `${CLOSET_GROUPS_URL}/${groupId}/usage`,
    params,
  );
  return data;
};

export const getClosetGroupsClofabFeature = async ({
  groupId,
}: APICLOSETGroupsDefaultParams): Promise<GetCLOSETGroupsClofabFeatureResponse> => {
  const { data } = await coreApiWithInterceptors.get(
    `${CLOSET_GROUPS_URL}/${groupId}/clofab-feature`,
  );
  return data;
};

// [EN] `PATCH`, not `PUT`, and it carries only the flag the user touched. A flag added
//      to the DTO later must not be zeroed out by a front end that predates it.
//      The response is the state after the write, so no re-fetch follows.
// [KO] `PUT` 이 아니라 `PATCH` 이고, 사용자가 건드린 플래그 하나만 싣는다. 나중에 DTO 에
//      플래그가 늘었을 때 그것을 모르는 옛 프론트가 0 으로 덮어쓰지 않게 하기 위해서다.
//      응답이 쓰기 후 상태라 재조회가 뒤따르지 않는다.
export const patchClosetGroupsClofabFeature = async ({
  groupId,
  ...params
}: PatchCLOSETGroupsClofabFeatureParams): Promise<GetCLOSETGroupsClofabFeatureResponse> => {
  const { data } = await coreApiWithInterceptors.patch(
    `${CLOSET_GROUPS_URL}/${groupId}/clofab-feature`,
    params,
  );
  return data;
};
